/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  Modal,
  TextInput,
  ScrollView,
  Dimensions,
  Alert,
  ActivityIndicator,
  Platform,
  Linking,
  RefreshControl,
  FlatList,
  Share,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Camera / Audio — gracefully degrade if expo-camera / expo-av not installed
let Camera, Audio;
try {
  Camera = require('expo-camera').Camera;
  Audio  = require('expo-av').Audio;
} catch (error) {
  console.warn('Camera/Audio modules not available:', error);
  Camera = {
    requestCameraPermissionsAsync: () => Promise.resolve({ status: 'granted' }),
    Constants: { Type: { front: 'front', back: 'back' } },
  };
  Audio = { requestPermissionsAsync: () => Promise.resolve({ status: 'granted' }) };
}

const { width, height } = Dimensions.get('window');

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const fmtDuration = (seconds) => {
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Combined scheduled-date+time formatter with a clean fallback. Returns
// 'Not scheduled' when the date is missing or unparseable so callers don't
// have to render a lonely " · " separator with empty values around it.
const fmtWhen = (iso) => {
  if (!iso) return 'Not scheduled';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Not scheduled';
  const day  = d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day} · ${time}`;
};

// Returns { label, isJoinable, isLive, msUntil, isPast }
const computeWindow = (scheduledAt, durationMinutes = 45) => {
  if (!scheduledAt) return { label: 'Awaiting schedule', isJoinable: false, isLive: false, msUntil: 0, isPast: false };
  const start = new Date(scheduledAt).getTime();
  const end   = start + (durationMinutes || 45) * 60 * 1000;
  const now   = Date.now();
  const msUntil = start - now;

  if (now >= start && now <= end) {
    return { label: 'Live now', isJoinable: true, isLive: true, msUntil: 0, isPast: false };
  }
  if (now > end) return { label: 'Ended', isJoinable: false, isLive: false, msUntil: 0, isPast: true };

  // Joinable window — 10 minutes before start
  const isJoinable = msUntil <= 10 * 60 * 1000;

  // Friendly label
  const m  = Math.floor(msUntil / 60000);
  const h  = Math.floor(m / 60);
  const d  = Math.floor(h / 24);
  let label;
  if (d > 0)  label = `Starts in ${d}d ${h % 24}h`;
  else if (h > 0) label = `Starts in ${h}h ${m % 60}m`;
  else if (m > 0) label = `Starts in ${m}m`;
  else            label = 'Starting now';

  return { label, isJoinable, isLive: false, msUntil, isPast: false };
};

const detectMeetingProvider = (url) => {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.includes('zoom.us'))            return 'Zoom';
  if (u.includes('teams.microsoft'))    return 'Microsoft Teams';
  if (u.includes('meet.google'))        return 'Google Meet';
  if (u.includes('meet.jit.si') || u.includes('jitsi')) return 'Jitsi Meet';
  if (u.includes('webex'))              return 'Webex';
  if (u.includes('whereby'))            return 'Whereby';
  if (u.includes('gotomeeting'))        return 'GoToMeeting';
  return 'Video Call';
};

// ─────────────────────────────────────────────────────────────────────────────
// INTERVIEW NORMALIZER
// Shared contract with the business/recruiter side: both read/write rows in
// `application_interviews`. This function makes the screen tolerant of the
// exact column names the table uses.
// ─────────────────────────────────────────────────────────────────────────────
// Pulls the scheduled timestamp out of a row, tolerant of (a) the various
// column names the recruiter side might write, (b) numeric epoch values
// (seconds OR milliseconds), and (c) completely unknown date-ish fields
// — falls back to scanning the row for any key matching /date|time|sched|
// when|start/ when the known names all miss. Logs a one-line summary of
// the row keys when nothing resolves so the field can be added to the
// known-name list on the next round of fixes.
const pickScheduledAt = (row) => {
  if (!row || typeof row !== 'object') return null;

  // 1) Known field names — covers snake_case and camelCase variants.
  const knownKeys = [
    'scheduled_at', 'scheduled_for', 'scheduledAt', 'scheduledFor',
    'interview_date', 'interviewDate', 'interview_datetime', 'interviewDateTime',
    'start_at', 'startAt', 'starts_at', 'startsAt',
    'start_time', 'startTime', 'starts_time', 'startsTime',
    'date_time', 'dateTime', 'datetime', 'when', 'scheduled',
  ];
  for (const k of knownKeys) {
    if (row[k] != null && row[k] !== '') return row[k];
  }

  // 2) Generic scan — any key that looks date/time-ish AND has a parseable value.
  for (const k of Object.keys(row)) {
    if (!/date|time|sched|when|start/i.test(k)) continue;
    const v = row[k];
    if (v == null || v === '') continue;
    // ISO string or number → parseable
    if (typeof v === 'string' || typeof v === 'number') {
      const d = new Date(typeof v === 'number' && v < 1e12 ? v * 1000 : v);
      if (!isNaN(d.getTime())) return v;
    }
  }

  // 3) Genuinely nothing — log keys so we can extend the known list later.
  console.warn('[VideoInterview] no scheduled time found on row. Available keys:', Object.keys(row).join(', '));
  return null;
};

const normalizeInterview = (row) => {
  if (!row) return null;
  // Accept Unix epoch (seconds OR ms) by normalizing to an ISO string,
  // so the rest of the screen can keep treating scheduledAt as a string.
  let scheduledAt = pickScheduledAt(row);
  if (typeof scheduledAt === 'number') {
    const ms = scheduledAt < 1e12 ? scheduledAt * 1000 : scheduledAt;
    const d = new Date(ms);
    scheduledAt = isNaN(d.getTime()) ? null : d.toISOString();
  }
  const durationMin = Number(row.duration_minutes ?? row.duration ?? 45) || 45;
  const status = String(row.status || 'scheduled').toLowerCase();
  const meetingUrl = row.meeting_url || row.meeting_link || row.video_link || row.join_url || '';
  const type = String(row.interview_type || row.type || (meetingUrl ? 'video' : 'video')).toLowerCase();

  // Interviewers — accept several shapes
  let interviewers = [];
  if (Array.isArray(row.interviewers))           interviewers = row.interviewers;
  else if (Array.isArray(row.interviewer_list))  interviewers = row.interviewer_list;
  else if (Array.isArray(row.panel))             interviewers = row.panel;
  else if (typeof row.interviewers === 'string') {
    try { const p = JSON.parse(row.interviewers); if (Array.isArray(p)) interviewers = p; } catch {}
  }
  interviewers = interviewers.map((p, i) => ({
    id:    p.id || p.user_id || `i_${i}`,
    name:  p.name || p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Interviewer',
    title: p.title || p.role || p.position || '',
    avatar: p.avatar || p.photo || p.avatar_url || null,
    email: p.email || '',
  }));

  // Job-side context: prefer JOINed columns, fall back to whatever's on the row
  const position = row.job_title || row.position || row.title || 'Interview';
  const company  = row.employer_name || row.company_name || row.company || 'Company';
  const companyLogo = row.employer_logo || row.company_logo || row.logo || null;

  // Full company envelope — every field the canonical /api/company-profile/:id
  // endpoint returns. Whatever the enrichment step found gets passed through,
  // so the prep / lobby / call / completion screens can render rich company
  // context without any extra fetches per render.
  const companyDetails = row._company || null;

  return {
    id:               String(row.id),
    applicationId:    row.application_id || row.applicationId || null,
    jobId:            row.job_id || null,
    companyId:        row.company_id || null,
    company,
    companyLogo,
    companyDetails,                     // { industry, headquarters, website, employee_count, description, country, city, ... }
    companyIndustry:  companyDetails?.industry      || row.company_industry      || '',
    companyLocation:  companyDetails?.headquarters  || companyDetails?.city      || row.company_location     || '',
    companyWebsite:   companyDetails?.website       || row.company_website       || '',
    companySize:      companyDetails?.employee_count|| row.company_employee_count|| null,
    companyAbout:     companyDetails?.description   || row.company_description   || '',
    position,
    type,                              // 'video', 'phone', 'in_person', 'async', 'one_way'
    scheduledAt,
    durationMinutes:  durationMin,
    meetingUrl,
    meetingProvider:  detectMeetingProvider(meetingUrl),
    meetingId:        row.meeting_id || '',
    meetingPassword:  row.meeting_password || row.meeting_passcode || '',
    location:         row.location || '',
    instructions:     row.instructions || row.notes_to_candidate || '',
    interviewers,
    status,                            // scheduled | in_progress | completed | cancelled | no_show
    roundNumber:      row.round_number || row.round || 1,
    recordingEnabled: row.recording_enabled !== false,
    recordingUrl:     row.recording_url || '',

    // Candidate-side fields (shared with business side, written by us)
    candidateNotes:    row.candidate_notes || '',
    candidateFeedback: row.candidate_feedback || row.candidate_rating || null,
    candidateJoinedAt: row.candidate_joined_at || null,
    candidateLeftAt:   row.candidate_left_at || null,
    candidateRescheduleReason:      row.candidate_reschedule_reason || '',
    candidatePreferredTimes:        row.candidate_preferred_times || '',
    candidateRescheduleRequestedAt: row.candidate_reschedule_requested_at || null,

    // Business-side fields (read-only for us, written by recruiter)
    interviewerNotes:    row.interviewer_notes || row.recruiter_notes || '',
    interviewerRating:   row.interviewer_rating || row.overall_score || null,
    decision:            row.decision || '',  // hired / rejected / next_round / on_hold

    createdAt:        row.created_at || null,
    updatedAt:        row.updated_at || null,
    _raw:             row,
  };
};

const isAsyncType = (t) => ['async', 'one_way', 'one-way', 'recorded'].includes(String(t || '').toLowerCase());

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function VideoInterviewScreen() {
  const profileCtx = useProfile();
  const navigation = useNavigation();
  const route      = useRoute();

  // If route param contains a specific interview id, jump straight to its
  // prep/detail view. Otherwise show the lobby.
  const focusedInterviewId = route.params?.interviewId || null;

  // ─── App-level state ───────────────────────────────────────────────────
  const [mode, setMode] = useState('lobby'); // 'lobby' | 'prep' | 'incall'
  const [interviews, setInterviews] = useState([]);
  const [selectedId, setSelectedId] = useState(focusedInterviewId);
  const [isLoading, setIsLoading]   = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [apiOffline, setApiOffline] = useState(false);
  const [tab, setTab] = useState('upcoming'); // 'upcoming' | 'past'
  const [profile, setProfile] = useState(null);

  // ─── In-call state (mirrors original screen) ───────────────────────────
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected]   = useState(false);
  const [connectionQuality, setConnectionQuality] = useState('good');
  const [isRecording, setIsRecording]   = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [hasPermission, setHasPermission] = useState(null);
  const [cameraType, setCameraType] = useState('front');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const cameraRef = useRef(null);

  const [interviewStartTime, setInterviewStartTime] = useState(null);
  const [interviewDuration, setInterviewDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);

  // ─── Notes / evaluation ───────────────────────────────────────────────
  const [showNotes, setShowNotes] = useState(false);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);
  const [prepNotes, setPrepNotes] = useState('');
  const [feedback, setFeedback] = useState({ rating: 0, comments: '', wouldRecommend: null });

  // ─── Live "now" tick — updates countdowns once a minute ───────────────
  const [, forceTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // ─── Mounted guard ────────────────────────────────────────────────────
  const isMounted = useRef(true);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  // ─── Profile hydration ────────────────────────────────────────────────
  const loadProfileData = useCallback(async () => {
    try {
      await ProfileManagementService.bootstrap();
      const r = await ProfileManagementService.getProfile();
      if (r?.success && r?.data) {
        const p = r.data;
        const profile = {
          id:    p.id || p.user_id || null,
          name:  p.name || p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim(),
          email: p.email || '',
          phone: p.phone || '',
          photo: p.photo || p.avatar_url || null,
        };
        if (isMounted.current) setProfile(profile);
        return profile;
      }
    } catch (e) { console.warn('Profile load failed:', e?.message); }
    return null;
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // FETCH INTERVIEWS — shared with business side via application_interviews
  // ─────────────────────────────────────────────────────────────────────
  const fetchInterviews = useCallback(async ({ silent = false, profileOverride = null } = {}) => {
    if (!silent) setIsLoading(true);
    setApiOffline(false);

    const useProfile = profileOverride || profile;
    const email = useProfile?.email || '';

    try {
      let rows = null;

      // Preferred — a job-seeker scoped endpoint that JOINs application_interviews
      // with applications + jobs + company_profiles. If the backend doesn't
      // expose this yet, we fall back to the generic /api/data route.
      try {
        const r = await apiFetch('/api/professional/my-interviews');
        if (r && !r.error) {
          rows = Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : null);
        }
      } catch {}

      if (!rows) {
        // Fallback 1: query application_interviews scoped by candidate_email
        try {
          const r = await apiFetch(
            `/api/data/application_interviews?candidate_email=${encodeURIComponent(email)}&sort=scheduled_at&order=desc&limit=100`
          );
          rows = Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : null);
        } catch {}
      }

      if (!rows) {
        // Fallback 2: pull all candidate's applications, then their interviews
        const appsResp = await apiFetch(
          `/api/data/applications?candidate_email=${encodeURIComponent(email)}&limit=100`
        );
        const apps = Array.isArray(appsResp) ? appsResp : (Array.isArray(appsResp?.data) ? appsResp.data : []);
        const appIds = apps.map(a => a.id).filter(Boolean);
        if (appIds.length) {
          const interviewLists = await Promise.allSettled(
            appIds.map(id =>
              apiFetch(`/api/data/application_interviews?application_id=${id}&limit=20`).then(r =>
                Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : [])
              )
            )
          );
          rows = interviewLists.flatMap(p => p.status === 'fulfilled' ? p.value : []);
        } else {
          rows = [];
        }
      }

      // ── Enrich missing job/company info per row (parallel, in stages) ──
      // Chain: row → (application → job → company). Each stage fires only
      // for rows that still need it. The KEY fix vs the old code is STAGE A:
      // resolving job_id from application_id. application_interviews holds
      // only application_id, not job_id, so without this step company_id
      // was never resolved and the company-profile fetch never fired —
      // which is why your logo / name were blank.

      // STAGE A: application_id → application row → job_id + company_id
      const needAppLookup = rows.filter(r => !r.job_id && r.application_id);
      if (needAppLookup.length) {
        const appMap = {};
        await Promise.allSettled(
          [...new Set(needAppLookup.map(r => r.application_id))].map(appId =>
            apiFetch(`/api/data/applications/${appId}`).then(res => {
              const row = Array.isArray(res) ? res[0]
                       : (res?.data && !Array.isArray(res.data)) ? res.data
                       : (Array.isArray(res?.data) ? res.data[0] : res);
              if (row?.id) appMap[appId] = row;
            }).catch(() => {})
          )
        );
        rows.forEach(r => {
          const a = appMap[r.application_id];
          if (a) {
            r.job_id      = r.job_id      || a.job_id;
            r.company_id  = r.company_id  || a.company_id;
            r.job_title   = r.job_title   || a.job_title;
          }
        });
        console.log('[VideoInterview] enriched', Object.keys(appMap).length, 'applications');
      }

      // STAGE B: job_id → job row → company_id + title
      const needJob = rows.filter(r => !r.company_id && r.job_id);
      if (needJob.length) {
        const jobMap = {};
        await Promise.allSettled(
          [...new Set(needJob.map(r => r.job_id).filter(Boolean))].map(jid =>
            apiFetch(`/api/data/jobs/${jid}`).then(res => {
              const row = Array.isArray(res) ? res[0]
                       : (res?.data && !Array.isArray(res.data)) ? res.data
                       : (Array.isArray(res?.data) ? res.data[0] : res);
              if (row?.id) jobMap[jid] = row;
            }).catch(() => {})
          )
        );
        rows.forEach(r => {
          const j = jobMap[r.job_id];
          if (j) {
            r.job_title  = r.job_title  || j.title;
            r.company_id = r.company_id || j.company_id;
          }
        });
        console.log('[VideoInterview] enriched', Object.keys(jobMap).length, 'jobs');
      }

      // STAGE C: company_id → full company profile.
      // ALWAYS fetch when company_id is present — even if the row already
      // has a name set — so we get the rich profile (industry / website /
      // description) not just the name. This is what the prep + lobby
      // cards need to render rich company context.
      const needCompany = rows.filter(r => r.company_id);
      console.log('[VideoInterview] rows needing company lookup:', needCompany.length, 'of', rows.length);
      if (needCompany.length) {
        const compMap = {};
        await Promise.allSettled(
          [...new Set(needCompany.map(r => r.company_id))].map(async cid => {
            // 1. /api/company-profile/:id (canonical, JOINed)
            try {
              const res = await apiFetch(`/api/company-profile/${cid}`);
              const row = (res?.data && !Array.isArray(res.data)) ? res.data
                       : (Array.isArray(res?.data) ? res.data[0] : res);
              if (row && (row.name || row.company_name)) {
                compMap[cid] = row;
                return;
              }
            } catch (e) { console.warn('[VideoInterview] /api/company-profile/' + cid + ':', e?.message); }
            // 2. /api/data/company_profile (singular table)
            try {
              const res = await apiFetch(`/api/data/company_profile?id=${cid}&limit=1`);
              const row = Array.isArray(res) ? res[0]
                       : Array.isArray(res?.data) ? res.data[0] : res;
              if (row?.name || row?.company_name) { compMap[cid] = row; return; }
            } catch {}
            // 3. /api/data/company_profiles (plural fallback)
            try {
              const res = await apiFetch(`/api/data/company_profiles?id=${cid}&limit=1`);
              const row = Array.isArray(res) ? res[0]
                       : Array.isArray(res?.data) ? res.data[0] : res;
              if (row) compMap[cid] = row;
            } catch {}
          })
        );
        rows.forEach(r => {
          const c = compMap[r.company_id];
          if (c) {
            r.employer_name = r.employer_name || c.name || c.company_name;
            r.employer_logo = r.employer_logo || c.logo;
            r._company = c;
            r.company_industry          = c.industry || null;
            r.company_location          = c.headquarters || c.city || null;
            r.company_website           = c.website || null;
            r.company_employee_count    = c.employee_count || null;
            r.company_description       = c.description || null;
          }
        });
        console.log('[VideoInterview] enriched', Object.keys(compMap).length, 'company profiles of', needCompany.length, 'rows needing them');
      } else {
        console.warn('[VideoInterview] NO rows had company_id — chain broken. Sample row keys:', rows[0] ? Object.keys(rows[0]).join(',') : 'no rows');
      }

      const normalized = rows.map(normalizeInterview).filter(Boolean);
      if (!isMounted.current) return;
      setInterviews(normalized);
      console.log(`✅ Loaded ${normalized.length} interviews`);
    } catch (e) {
      console.error('❌ Interview fetch failed:', e);
      if (isMounted.current) setApiOffline(true);
    } finally {
      if (isMounted.current && !silent) setIsLoading(false);
    }
  }, [profile]);

  // ─────────────────────────────────────────────────────────────────────
  // WRITEBACK — patches the row that both sides read
  // ─────────────────────────────────────────────────────────────────────
  const patchInterview = async (id, updates) => {
    try {
      const r = await apiFetch(`/api/data/application_interviews/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      if (r?.error) throw new Error(r.error);
      // Optimistic local update
      setInterviews(prev => prev.map(iv => iv.id === id ? { ...iv, ...mapUpdatesToCamel(updates), _raw: { ...iv._raw, ...updates } } : iv));
      return true;
    } catch (e) {
      console.warn('Interview patch failed:', e?.message);
      Alert.alert('Sync failed', 'Your changes will retry on reconnect.');
      return false;
    }
  };

  const mapUpdatesToCamel = (u) => ({
    ...(u.status              !== undefined ? { status: String(u.status).toLowerCase() } : {}),
    ...(u.candidate_notes     !== undefined ? { candidateNotes: u.candidate_notes } : {}),
    ...(u.candidate_feedback  !== undefined ? { candidateFeedback: u.candidate_feedback } : {}),
    ...(u.candidate_joined_at !== undefined ? { candidateJoinedAt: u.candidate_joined_at } : {}),
    ...(u.candidate_left_at   !== undefined ? { candidateLeftAt: u.candidate_left_at } : {}),
    ...(u.candidate_reschedule_reason       !== undefined ? { candidateRescheduleReason:      u.candidate_reschedule_reason } : {}),
    ...(u.candidate_preferred_times         !== undefined ? { candidatePreferredTimes:        u.candidate_preferred_times } : {}),
    ...(u.candidate_reschedule_requested_at !== undefined ? { candidateRescheduleRequestedAt: u.candidate_reschedule_requested_at } : {}),
  });

  // ─────────────────────────────────────────────────────────────────────
  // INITIAL LOAD
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const p = await loadProfileData();
      await fetchInterviews({ profileOverride: p });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh on focus (recruiter may have updated something)
  useFocusEffect(useCallback(() => {
    if (profile?.email) fetchInterviews({ silent: true, profileOverride: profile });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.email]));

  // If we landed with a specific interview id, jump straight to prep view
  useEffect(() => {
    if (!focusedInterviewId || !interviews.length) return;
    const exists = interviews.find(i => i.id === String(focusedInterviewId));
    if (exists) {
      setSelectedId(exists.id);
      setPrepNotes(exists.candidateNotes || '');
      setMode('prep');
    }
  }, [focusedInterviewId, interviews]);

  // ─────────────────────────────────────────────────────────────────────
  // SELECTED INTERVIEW + PARTITIONS
  // ─────────────────────────────────────────────────────────────────────
  const selected = useMemo(
    () => interviews.find(i => i.id === selectedId) || null,
    [interviews, selectedId]
  );

  const upcoming = useMemo(
    () => interviews
      .filter(i => ['scheduled', 'in_progress'].includes(i.status))
      .sort((a, b) => new Date(a.scheduledAt || 0) - new Date(b.scheduledAt || 0)),
    [interviews]
  );
  const past = useMemo(
    () => interviews
      .filter(i => ['completed', 'cancelled', 'no_show'].includes(i.status))
      .sort((a, b) => new Date(b.scheduledAt || 0) - new Date(a.scheduledAt || 0)),
    [interviews]
  );

  // ─────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────
  const openInterviewPrep = (iv) => {
    setSelectedId(iv.id);
    setPrepNotes(iv.candidateNotes || '');
    setFeedback({
      rating: iv.candidateFeedback?.rating || 0,
      comments: iv.candidateFeedback?.comments || '',
      wouldRecommend: iv.candidateFeedback?.would_recommend ?? null,
    });
    setMode('prep');
  };

  const savePrepNotes = async () => {
    if (!selected) return;
    const ok = await patchInterview(selected.id, { candidate_notes: prepNotes });
    if (ok) Alert.alert('Notes saved', 'Your prep notes are saved against this interview.');
  };

  // Resolve a meeting URL into something we can actually open. The
  // recruiter side auto-generates `https://meet.google.com/<random>` when
  // no URL is supplied, but those are not real Meet codes — Google rejects
  // them with "meeting code not found". Detect that pattern (real codes
  // are `xxx-yyyy-zzz` with dashes) and fall back to Jitsi Meet, which
  // accepts ANY room name and works in browser / Jitsi app / Jitsi web.
  //
  // Also: empty URL → still resolvable, by generating a deterministic
  // Jitsi room from the interview ID. So candidates can always join.
  const resolveMeetingUrl = (raw, interviewId) => {
    let url = String(raw || '').trim().replace(/[.,;]+$/, '');
    // Auto-generated fake Google Meet URL?
    const meetMatch = url.match(/meet\.google\.com\/([\w-]+)/i);
    if (meetMatch) {
      const code = meetMatch[1];
      // Real Google Meet codes follow the `abc-defg-hij` pattern.
      if (!/^[a-z]{3,4}-[a-z]{3,4}-[a-z]{3,4}$/i.test(code)) {
        const jitsi = `https://meet.jit.si/jobeng-interview-${interviewId}`;
        console.log('[VideoInterview] meet.google.com URL looks fake (', code, ') — using Jitsi:', jitsi);
        return jitsi;
      }
    }
    // Empty or completely invalid → spin up a Jitsi room.
    if (!url) return `https://meet.jit.si/jobeng-interview-${interviewId}`;
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    return url;
  };

  const handleJoin = async () => {
    if (!selected) return;
    const w = computeWindow(selected.scheduledAt, selected.durationMinutes);
    if (!w.isJoinable) {
      Alert.alert('Not yet joinable', 'You can join from 10 minutes before the scheduled start.');
      return;
    }

    // Mark joined
    const nowIso = new Date().toISOString();
    await patchInterview(selected.id, {
      status: 'in_progress',
      candidate_joined_at: nowIso,
    });

    if (isAsyncType(selected.type)) {
      // Async/one-way → stay in-app and use camera
      setMode('incall');
      setupPermissions();
      setInterviewStartTime(Date.now());
      setIsConnected(true);
      return;
    }

    // Live → open external meeting URL with smart fallback for bad/missing URLs.
    const url = resolveMeetingUrl(selected.meetingUrl, selected.id);
    console.log('[VideoInterview] joining meeting — resolved URL:', url);
    console.log('[VideoInterview] raw URL was:', selected.meetingUrl);

    // Skip canOpenURL: it sometimes returns false on iOS even for valid http(s)
    // URLs (Info.plist scheme allow-list), and that returning false silently
    // blocks a perfectly good URL. http(s) always has a handler (Safari/Chrome)
    // on every device, so just call openURL and catch any failure.
    try {
      await Linking.openURL(url);
      console.log('[VideoInterview] openURL succeeded');
    } catch (e) {
      console.warn('[VideoInterview] meeting openURL threw:', e?.message);
      // Final fallback — Jitsi room. Universally supported.
      const jitsi = `https://meet.jit.si/jobeng-interview-${selected.id}`;
      console.log('[VideoInterview] falling back to:', jitsi);
      try {
        await Linking.openURL(jitsi);
      } catch (e2) {
        console.error('[VideoInterview] Jitsi fallback also threw:', e2?.message);
        Alert.alert(
          'Could not open video call',
          `Tried: ${url}\nThen: ${jitsi}\nError: ${e2?.message || e?.message || 'unknown'}\n\nCopy the link above and open it in your browser manually.`,
        );
      }
    }
  };

  // Keep the old branches for in-person / no-link cases.
  const _legacyJoinFallback = () => {
    if (selected?.location) {
      Alert.alert('In-person interview', selected.location);
    } else {
      Alert.alert('No join link', 'The recruiter has not yet shared a meeting link.');
    }
  };

  const handleMarkComplete = () => {
    if (!selected) return;
    setShowEvaluation(true);
  };

  const submitFeedback = async () => {
    if (!selected) return;
    const nowIso = new Date().toISOString();
    const payload = {
      status: 'completed',
      candidate_left_at: nowIso,
      candidate_feedback: {
        rating: feedback.rating,
        comments: feedback.comments,
        would_recommend: feedback.wouldRecommend,
        submitted_at: nowIso,
      },
    };
    const ok = await patchInterview(selected.id, payload);
    if (ok) {
      setShowEvaluation(false);
      setMode('lobby');
      Alert.alert('Thanks!', 'Your feedback has been shared with the recruiter.');
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // RESCHEDULE — hand off to the standalone RescheduleInterview screen
  // where the candidate picks a new date/time/reason. That screen writes
  // straight to `application_interviews` (status='reschedule_requested',
  // candidate_proposed_at, candidate_reschedule_reason, …), so when we
  // come back the lobby re-fetch will reflect the new state.
  // ─────────────────────────────────────────────────────────────────────
  const openRescheduleScreen = () => {
    if (!selected) return;
    navigation.navigate('RescheduleInterview', {
      interview:    selected,
      interviewId:  selected.id,
      source:       'VideoInterview',
      returnScreen: 'VideoInterview',
    });
  };

  const shareMeetingLink = async () => {
    if (!selected?.meetingUrl) return;
    try {
      await Share.share({
        message: `${selected.position} interview at ${selected.company}\n${fmtDate(selected.scheduledAt)} at ${fmtTime(selected.scheduledAt)}\n\nJoin: ${selected.meetingUrl}`,
      });
    } catch {}
  };

  // ─────────────────────────────────────────────────────────────────────
  // IN-CALL: permissions, timers, recording
  // ─────────────────────────────────────────────────────────────────────
  const setupPermissions = useCallback(async () => {
    try {
      const cameraResult = await Camera.requestCameraPermissionsAsync();
      const audioResult  = await Audio.requestPermissionsAsync();
      if (cameraResult.status === 'granted' && audioResult.status === 'granted') {
        setHasPermission(true);
      } else {
        setHasPermission(false);
      }
    } catch (e) {
      console.error('Permission error:', e);
      setHasPermission(false);
    }
  }, []);

  useEffect(() => {
    if (mode !== 'incall' || !interviewStartTime) return;
    const t = setInterval(() => {
      const elapsed = Math.floor((Date.now() - interviewStartTime) / 1000);
      setInterviewDuration(elapsed);
      if (isRecording) setRecordingDuration(elapsed);
    }, 1000);
    return () => clearInterval(t);
  }, [mode, interviewStartTime, isRecording]);

  const startRecording = async () => {
    setIsRecording(true);
    setRecordingDuration(0);
  };
  const stopRecording = async () => { setIsRecording(false); };

  const exitCallToReview = async () => {
    setShowEndConfirmation(false);
    if (isRecording) await stopRecording();
    setShowEvaluation(true);
  };

  // ─────────────────────────────────────────────────────────────────────
  // RENDER — LOBBY
  // ─────────────────────────────────────────────────────────────────────
  const renderInterviewCard = (iv) => {
    const w = computeWindow(iv.scheduledAt, iv.durationMinutes);
    const statusColor = {
      scheduled:   '#0e4e96',
      in_progress: '#16a34a',
      completed:   '#64748B',
      cancelled:   '#dc2626',
      no_show:     '#dc2626',
    }[iv.status] || '#64748B';

    // Pretty-print the scheduled date for the prominent block:
    //   "Thursday, 22 May 2026"   (full day name + day + month + year)
    //   "14:30 – 15:30"           (start – end, computed from durationMinutes)
    let prettyDay = '', prettyDate = '', prettyTimeRange = '';
    if (iv.scheduledAt) {
      const d = new Date(iv.scheduledAt);
      if (!isNaN(d.getTime())) {
        prettyDay  = d.toLocaleDateString('en-GB', { weekday: 'long' });
        prettyDate = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        const endTime = new Date(d.getTime() + (iv.durationMinutes || 45) * 60 * 1000);
        const t1 = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        const t2 = endTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        prettyTimeRange = `${t1} – ${t2}`;
      }
    }

    const isScheduledLike = iv.status === 'scheduled' || iv.status === 'in_progress';

    return (
      <TouchableOpacity
        key={iv.id}
        style={styles.lobbyCard}
        onPress={() => openInterviewPrep(iv)}
        activeOpacity={0.85}
      >
        <View style={styles.lobbyCardHeader}>
          <Image
            source={iv.companyLogo ? { uri: iv.companyLogo } : require('../../../assets/default-company-logo.png')}
            style={styles.lobbyLogo}
            resizeMode="contain"
            defaultSource={require('../../../assets/default-company-logo.png')}
          />
          <View style={{ flex: 1 }}>
            <Text style={styles.lobbyPosition} numberOfLines={1}>{iv.position}</Text>
            <Text style={styles.lobbyCompany} numberOfLines={1}>
              {iv.company}{iv.companyIndustry ? ` · ${iv.companyIndustry}` : ''}
            </Text>
          </View>
          <View style={[styles.statusPill, { backgroundColor: statusColor + '20' }]}>
            <Text style={[styles.statusPillText, { color: statusColor }]}>
              {iv.status.replace('_', ' ')}
            </Text>
          </View>
        </View>

        {/* Prominent scheduled block — bigger, easier to read at a glance.
            Shows day name, full date, and start–end time range. The live
            countdown sits inline on the right so candidates always see
            both "when" and "how long until". */}
        {isScheduledLike && iv.scheduledAt ? (
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: w.isLive ? '#16a34a10' : '#0e4e9608',
            borderWidth: 1,
            borderColor: w.isLive ? '#16a34a30' : '#0e4e9620',
            borderRadius: 10,
            paddingVertical: 10,
            paddingHorizontal: 12,
            marginTop: 8,
          }}>
            <View style={{
              width: 44, height: 44, borderRadius: 10,
              backgroundColor: w.isLive ? '#16a34a' : '#0e4e96',
              alignItems: 'center', justifyContent: 'center', marginRight: 12,
            }}>
              <Ionicons name="calendar" size={22} color="#FFF" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 15, fontWeight: '700', color: '#0f172a' }}>
                {prettyDay}
              </Text>
              <Text style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
                {prettyDate}
              </Text>
              <Text style={{ fontSize: 14, fontWeight: '600', color: '#0f172a', marginTop: 4 }}>
                {prettyTimeRange}
              </Text>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: w.isLive ? '#16a34a' : '#0e4e96',
                marginTop: 4,
              }}>
                {w.isLive ? '● Live now' : w.label}
              </Text>
            </View>
          </View>
        ) : null}

        <View style={styles.lobbyMeta}>
          {!isScheduledLike && iv.scheduledAt ? (
            <View style={styles.lobbyMetaItem}>
              <Ionicons name="calendar-outline" size={14} color="#64748B" />
              <Text style={styles.lobbyMetaText}>{fmtWhen(iv.scheduledAt)}</Text>
            </View>
          ) : null}
          <View style={styles.lobbyMetaItem}>
            <Ionicons name={iv.meetingUrl ? 'videocam-outline' : iv.location ? 'location-outline' : 'help-circle-outline'} size={14} color="#64748B" />
            <Text style={styles.lobbyMetaText} numberOfLines={1}>
              {iv.meetingProvider || (isAsyncType(iv.type) ? 'One-way recording' : iv.location || 'Awaiting details')}
            </Text>
          </View>
          <View style={styles.lobbyMetaItem}>
            <Ionicons name="time-outline" size={14} color="#64748B" />
            <Text style={styles.lobbyMetaText}>{iv.durationMinutes} min · Round {iv.roundNumber}</Text>
          </View>
        </View>

        {iv.interviewers.length > 0 && (
          <View style={styles.interviewerStrip}>
            {iv.interviewers.slice(0, 3).map((p, idx) => (
              <View key={p.id || idx} style={[styles.interviewerAvatar, { marginLeft: idx === 0 ? 0 : -8 }]}>
                {p.avatar ? (
                  <Image source={{ uri: p.avatar }} style={{ width: 24, height: 24, borderRadius: 12 }} />
                ) : (
                  <Text style={styles.interviewerInitial}>{p.name.charAt(0).toUpperCase()}</Text>
                )}
              </View>
            ))}
            <Text style={styles.interviewerList} numberOfLines={1}>
              {iv.interviewers.map(p => p.name).join(', ')}
            </Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  const renderLobby = () => (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      <View style={styles.header}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Interviews</Text>
        <TouchableOpacity style={styles.backBtn} onPress={() => fetchInterviews({ silent: false })}>
          <Ionicons name="refresh" size={22} color="#fff" />
        </TouchableOpacity>
      </View>

      {apiOffline && (
        <View style={styles.offlineBanner}>
          <Ionicons name="cloud-offline-outline" size={16} color="#856404" />
          <Text style={styles.offlineText}>Couldn't reach server. Pull down to retry.</Text>
        </View>
      )}

      <View style={styles.tabBar}>
        <TouchableOpacity
          style={[styles.tabBtn, tab === 'upcoming' && styles.tabBtnActive]}
          onPress={() => setTab('upcoming')}
        >
          <Text style={[styles.tabBtnText, tab === 'upcoming' && styles.tabBtnTextActive]}>
            Upcoming ({upcoming.length})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tabBtn, tab === 'past' && styles.tabBtnActive]}
          onPress={() => setTab('past')}
        >
          <Text style={[styles.tabBtnText, tab === 'past' && styles.tabBtnTextActive]}>
            Past ({past.length})
          </Text>
        </TouchableOpacity>
      </View>

      {isLoading ? (
        <View style={styles.center}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.muted}>Loading your interviews…</Text>
        </View>
      ) : (
        <FlatList
          data={tab === 'upcoming' ? upcoming : past}
          keyExtractor={i => i.id}
          renderItem={({ item }) => renderInterviewCard(item)}
          contentContainerStyle={{ padding: 16 }}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={async () => {
                setIsRefreshing(true);
                await fetchInterviews({ silent: true });
                setIsRefreshing(false);
              }}
              colors={['#0e4e96']}
            />
          }
          ListEmptyComponent={
            <View style={styles.center}>
              <Ionicons name="calendar-outline" size={48} color="#CBD5E1" />
              <Text style={styles.emptyTitle}>
                {tab === 'upcoming' ? 'No upcoming interviews' : 'No past interviews'}
              </Text>
              <Text style={styles.muted}>
                {tab === 'upcoming'
                  ? 'When a recruiter schedules an interview with you, it\'ll appear here.'
                  : 'Completed interviews will appear here.'}
              </Text>
            </View>
          }
        />
      )}
    </SafeAreaView>
  );

  // ─────────────────────────────────────────────────────────────────────
  // RENDER — PREP / DETAIL
  // ─────────────────────────────────────────────────────────────────────
  const renderPrep = () => {
    if (!selected) return null;
    const w = computeWindow(selected.scheduledAt, selected.durationMinutes);
    const ended = selected.status === 'completed' || selected.status === 'cancelled' || selected.status === 'no_show';

    // Resolve the meeting URL the same way handleJoin will, then re-detect
    // the provider from the resolved link. This keeps the button label
    // ("Open in …") honest — e.g. a fake meet.google.com placeholder gets
    // rewritten to a Jitsi room, so the label reads "Open in Jitsi Meet"
    // instead of misleading the candidate with "Open in Google Meet".
    const resolvedVideoUrl      = selected.meetingUrl ? resolveMeetingUrl(selected.meetingUrl, selected.id) : '';
    const resolvedVideoProvider = resolvedVideoUrl ? detectMeetingProvider(resolvedVideoUrl) : null;

    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

        <View style={styles.header}>
          <TouchableOpacity style={styles.backBtn} onPress={() => setMode('lobby')}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle} numberOfLines={1}>{selected.position}</Text>
          <TouchableOpacity style={styles.backBtn} onPress={shareMeetingLink} disabled={!selected.meetingUrl}>
            <Ionicons name="share-outline" size={22} color={selected.meetingUrl ? '#fff' : '#94A3B8'} />
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={{ paddingBottom: 32 }}>
          {/* Hero */}
          <LinearGradient colors={['#0e4e96', '#1d4ed8']} style={styles.prepHero}>
            <Image
              source={selected.companyLogo ? { uri: selected.companyLogo } : require('../../../assets/default-company-logo.png')}
              style={styles.prepLogo}
              resizeMode="contain"
            />
            <Text style={styles.prepCompany}>{selected.company}</Text>
            <Text style={styles.prepRole}>{selected.position}</Text>

            {/* Company chips — only render fields that exist so we never
                show empty pills. Pulled from the full company profile
                enriched by the load step. */}
            {(selected.companyIndustry || selected.companyLocation || selected.companySize) ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 6, marginTop: 8 }}>
                {selected.companyIndustry ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 }}>
                    <Ionicons name="briefcase-outline" size={11} color="#fff" />
                    <Text style={{ color: '#fff', fontSize: 11, marginLeft: 4 }}>{selected.companyIndustry}</Text>
                  </View>
                ) : null}
                {selected.companyLocation ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 }}>
                    <Ionicons name="location-outline" size={11} color="#fff" />
                    <Text style={{ color: '#fff', fontSize: 11, marginLeft: 4 }}>{selected.companyLocation}</Text>
                  </View>
                ) : null}
                {selected.companySize ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 }}>
                    <Ionicons name="people-outline" size={11} color="#fff" />
                    <Text style={{ color: '#fff', fontSize: 11, marginLeft: 4 }}>{selected.companySize} team</Text>
                  </View>
                ) : null}
              </View>
            ) : null}

            <View style={styles.prepCountdown}>
              <Ionicons
                name={w.isLive ? 'radio-button-on' : ended ? 'checkmark-circle' : 'time'}
                size={16}
                color="#fff"
              />
              <Text style={styles.prepCountdownText}>
                {ended ? selected.status.replace('_', ' ').toUpperCase() : w.label}
              </Text>
            </View>

            {!ended && (
              <TouchableOpacity
                style={[styles.joinBtn, !w.isJoinable && styles.joinBtnDisabled]}
                onPress={handleJoin}
                disabled={!w.isJoinable}
              >
                <Ionicons
                  name={isAsyncType(selected.type) ? 'videocam' : 'open-outline'}
                  size={18}
                  color={w.isJoinable ? '#0e4e96' : '#fff'}
                />
                <Text style={[styles.joinBtnText, !w.isJoinable && { color: '#fff' }]}>
                  {isAsyncType(selected.type)
                    ? 'Start Recording'
                    : selected.meetingUrl
                    ? `Open in ${resolvedVideoProvider || 'Video Call'}`
                    : 'Join'}
                </Text>
              </TouchableOpacity>
            )}

            {!ended && selected.status === 'in_progress' && (
              <TouchableOpacity style={styles.markCompleteBtn} onPress={handleMarkComplete}>
                <Text style={styles.markCompleteBtnText}>Mark as Completed</Text>
              </TouchableOpacity>
            )}

            {!ended && selected.status !== 'in_progress' && selected.status !== 'reschedule_requested' && (
              <TouchableOpacity style={styles.rescheduleBtn} onPress={openRescheduleScreen}>
                <Ionicons name="calendar-outline" size={14} color="#fff" />
                <Text style={styles.rescheduleBtnText}>Request Reschedule</Text>
              </TouchableOpacity>
            )}

            {!ended && selected.status === 'reschedule_requested' && (
              <TouchableOpacity style={[styles.rescheduleBtn, styles.reschedulePendingBtn]} onPress={openRescheduleScreen}>
                <Ionicons name="time-outline" size={14} color="#fff" />
                <Text style={styles.rescheduleBtnText}>Reschedule pending · Tap to edit</Text>
              </TouchableOpacity>
            )}
          </LinearGradient>

          {/* Details */}
          <View style={styles.prepSection}>
            <Text style={styles.prepSectionTitle}>Details</Text>
            <DetailRow icon="calendar-outline" label="When"     value={fmtWhen(selected.scheduledAt)} />
            <DetailRow icon="time-outline"     label="Duration" value={`${selected.durationMinutes} minutes`} />
            <DetailRow icon="layers-outline"   label="Round"    value={`Round ${selected.roundNumber}`} />
            <DetailRow icon="cube-outline"     label="Type"     value={selected.type.replace('_', ' ')} />
            {(resolvedVideoProvider || selected.meetingProvider) && (
              <DetailRow icon="videocam-outline" label="Platform" value={resolvedVideoProvider || selected.meetingProvider} />
            )}
            {selected.meetingId && (
              <DetailRow icon="key-outline" label="Meeting ID" value={selected.meetingId} />
            )}
            {selected.meetingPassword && (
              <DetailRow icon="lock-closed-outline" label="Password" value={selected.meetingPassword} />
            )}
            {selected.location && (
              <DetailRow icon="location-outline" label="Location" value={selected.location} />
            )}
          </View>

          {/* About the company — populated from the canonical company-profile
              endpoint via the load step. Renders only when we actually have
              content; otherwise the section is skipped. */}
          {(selected.companyAbout || selected.companyWebsite) ? (
            <View style={styles.prepSection}>
              <Text style={styles.prepSectionTitle}>About {selected.company}</Text>
              {selected.companyAbout ? (
                <Text style={{ fontSize: 14, lineHeight: 20, color: '#374151', marginBottom: selected.companyWebsite ? 10 : 0 }}>
                  {selected.companyAbout}
                </Text>
              ) : null}
              {selected.companyWebsite ? (
                <TouchableOpacity
                  onPress={async () => {
                    // Sanitize: trim whitespace, prepend https:// if missing,
                    // strip trailing punctuation that sometimes sneaks in
                    // from copy-paste ("jobeng.in." or "jobeng.in,").
                    let u = String(selected.companyWebsite || '').trim().replace(/[.,;]+$/, '');
                    if (!u) return;
                    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
                    console.log('[VideoInterview] opening website:', u);
                    try {
                      await Linking.openURL(u);
                    } catch (e) {
                      console.warn('[VideoInterview] openURL failed:', e?.message);
                      Alert.alert('Could not open', e?.message || 'The link is invalid or your browser is unavailable.');
                    }
                  }}
                  style={{ flexDirection: 'row', alignItems: 'center' }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons name="globe-outline" size={14} color="#0e4e96" />
                  <Text style={{ color: '#0e4e96', fontWeight: '600', marginLeft: 6, fontSize: 13, textDecorationLine: 'underline' }}>
                    {selected.companyWebsite}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}

          {/* Instructions from recruiter */}
          {selected.instructions ? (
            <View style={styles.prepSection}>
              <Text style={styles.prepSectionTitle}>Instructions from Recruiter</Text>
              <View style={styles.instructionsBox}>
                <Text style={styles.instructionsText}>{selected.instructions}</Text>
              </View>
            </View>
          ) : null}

          {/* Interviewers */}
          {selected.interviewers.length > 0 && (
            <View style={styles.prepSection}>
              <Text style={styles.prepSectionTitle}>Interviewer{selected.interviewers.length > 1 ? 's' : ''}</Text>
              {selected.interviewers.map((p, idx) => (
                <View key={p.id || idx} style={styles.interviewerRow}>
                  <View style={styles.interviewerRowAvatar}>
                    {p.avatar ? (
                      <Image source={{ uri: p.avatar }} style={{ width: 40, height: 40, borderRadius: 20 }} />
                    ) : (
                      <Text style={styles.interviewerRowInitial}>{p.name.charAt(0).toUpperCase()}</Text>
                    )}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.interviewerRowName}>{p.name}</Text>
                    {p.title ? <Text style={styles.interviewerRowTitle}>{p.title}</Text> : null}
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Personal prep notes — shared back to recruiter via candidate_notes */}
          <View style={styles.prepSection}>
            <Text style={styles.prepSectionTitle}>Your Prep Notes</Text>
            <Text style={styles.prepSectionSub}>
              Visible to you and the recruiter. Use this for questions you'd like to ask, accommodations you need, or any context to share.
            </Text>
            <TextInput
              style={styles.notesInput}
              placeholder="Questions to ask, key points to bring up, things to remember…"
              value={prepNotes}
              onChangeText={setPrepNotes}
              multiline
              numberOfLines={5}
              textAlignVertical="top"
            />
            <TouchableOpacity style={styles.savePrepBtn} onPress={savePrepNotes}>
              <Ionicons name="save-outline" size={16} color="#fff" />
              <Text style={styles.savePrepBtnText}>Save Notes</Text>
            </TouchableOpacity>
          </View>

          {/* Post-interview: feedback from recruiter (read-only) */}
          {ended && (selected.interviewerNotes || selected.interviewerRating || selected.decision) && (
            <View style={styles.prepSection}>
              <Text style={styles.prepSectionTitle}>Recruiter Feedback</Text>
              {selected.decision ? (
                <DetailRow icon="trophy-outline" label="Decision" value={selected.decision.replace('_', ' ')} />
              ) : null}
              {selected.interviewerRating != null ? (
                <DetailRow icon="star-outline" label="Rating" value={`${selected.interviewerRating} / 5`} />
              ) : null}
              {selected.interviewerNotes ? (
                <View style={styles.instructionsBox}>
                  <Text style={styles.instructionsText}>{selected.interviewerNotes}</Text>
                </View>
              ) : null}
            </View>
          )}

          {/* Recording */}
          {selected.recordingUrl ? (
            <View style={styles.prepSection}>
              <Text style={styles.prepSectionTitle}>Recording</Text>
              <TouchableOpacity
                style={styles.recordingBtn}
                onPress={() => Linking.openURL(selected.recordingUrl)}
              >
                <Ionicons name="play-circle-outline" size={20} color="#0e4e96" />
                <Text style={styles.recordingBtnText}>Open Recording</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </ScrollView>

        {renderEvaluationModal()}
      </SafeAreaView>
    );
  };

  // ─────────────────────────────────────────────────────────────────────
  // RENDER — IN-CALL (async / one-way recording)
  // ─────────────────────────────────────────────────────────────────────
  const renderInCall = () => {
    if (!selected) return null;

    if (hasPermission === null) {
      return (
        <View style={styles.callRoot}>
          <ActivityIndicator size={36} color="#fff" />
          <Text style={[styles.muted, { color: '#fff' }]}>Requesting permissions…</Text>
        </View>
      );
    }
    if (hasPermission === false) {
      return (
        <View style={styles.callRoot}>
          <Ionicons name="videocam-off" size={64} color="#fff" />
          <Text style={[styles.muted, { color: '#fff', marginBottom: 16, textAlign: 'center' }]}>
            Camera & microphone permissions required for the recording.
          </Text>
          <TouchableOpacity style={styles.permBtn} onPress={() => Linking.openSettings()}>
            <Text style={styles.permBtnText}>Open Settings</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.permBtn, { marginTop: 8, backgroundColor: '#475569' }]} onPress={() => setMode('prep')}>
            <Text style={styles.permBtnText}>Back</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={styles.callRoot}>
        <StatusBar barStyle="light-content" backgroundColor="#000" />

        {/* Top bar */}
        <View style={styles.callTopBar}>
          <View style={styles.connectionDotRow}>
            <View style={[styles.connectionDot, { backgroundColor: '#16a34a' }]} />
            <Text style={styles.connectionText}>Connected · {connectionQuality}</Text>
          </View>
          <View>
            <Text style={styles.callTimer}>{fmtDuration(interviewDuration)}</Text>
            <Text style={styles.callSub}>{selected.company}</Text>
          </View>
          {isRecording ? (
            <View style={styles.recBadge}>
              <View style={styles.recDot} />
              <Text style={styles.recText}>REC {fmtDuration(recordingDuration)}</Text>
            </View>
          ) : <View style={{ width: 80 }} />}
        </View>

        {/* Camera placeholder */}
        <TouchableOpacity style={styles.cameraArea} onPress={() => setShowControls(s => !s)}>
          {isVideoEnabled ? (
            <View style={styles.cameraPlaceholder}>
              <Ionicons name="videocam" size={80} color="#475569" />
              <Text style={styles.cameraPlaceholderText}>
                Camera preview ({cameraType})
              </Text>
            </View>
          ) : (
            <View style={styles.cameraPlaceholder}>
              <Ionicons name="videocam-off" size={64} color="#475569" />
              <Text style={styles.cameraPlaceholderText}>Video off</Text>
            </View>
          )}
        </TouchableOpacity>

        {/* Controls */}
        {showControls && (
          <View style={styles.callControls}>
            <CallControl
              icon={isMuted ? 'mic-off' : 'mic'}
              active={!isMuted}
              onPress={() => setIsMuted(m => !m)}
            />
            <CallControl
              icon={isVideoEnabled ? 'videocam' : 'videocam-off'}
              active={isVideoEnabled}
              onPress={() => setIsVideoEnabled(v => !v)}
            />
            <CallControl
              icon="camera-reverse-outline"
              active
              onPress={() => setCameraType(t => t === 'front' ? 'back' : 'front')}
            />
            <CallControl
              icon={isRecording ? 'stop-circle' : 'recording-outline'}
              active={isRecording}
              danger={isRecording}
              onPress={() => isRecording ? stopRecording() : startRecording()}
            />
            <CallControl
              icon="document-text-outline"
              active
              onPress={() => setShowNotes(true)}
            />
            <TouchableOpacity
              style={[styles.callBtn, styles.endCallBtn]}
              onPress={() => setShowEndConfirmation(true)}
            >
              <Ionicons name="call" size={24} color="#fff" style={{ transform: [{ rotate: '135deg' }] }} />
            </TouchableOpacity>
          </View>
        )}

        {/* Notes modal */}
        <Modal visible={showNotes} animationType="slide" transparent onRequestClose={() => setShowNotes(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modal}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Interview Notes</Text>
                <TouchableOpacity onPress={() => setShowNotes(false)}>
                  <Ionicons name="close" size={24} color="#64748B" />
                </TouchableOpacity>
              </View>
              <Text style={styles.muted}>Saved against this interview. The recruiter will see it.</Text>
              <TextInput
                style={[styles.notesInput, { marginTop: 12 }]}
                placeholder="Notes during/after the call…"
                value={prepNotes}
                onChangeText={setPrepNotes}
                multiline
                numberOfLines={6}
                textAlignVertical="top"
              />
              <TouchableOpacity
                style={[styles.savePrepBtn, { marginTop: 12 }]}
                onPress={async () => { await savePrepNotes(); setShowNotes(false); }}
              >
                <Text style={styles.savePrepBtnText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>

        {/* End confirmation */}
        <Modal visible={showEndConfirmation} transparent animationType="fade" onRequestClose={() => setShowEndConfirmation(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modal}>
              <Text style={styles.modalTitle}>End interview?</Text>
              <Text style={[styles.muted, { marginTop: 8 }]}>
                You'll be asked to share quick feedback on your experience.
              </Text>
              <View style={{ flexDirection: 'row', marginTop: 16 }}>
                <TouchableOpacity
                  style={[styles.permBtn, { flex: 1, marginRight: 8, backgroundColor: '#475569' }]}
                  onPress={() => setShowEndConfirmation(false)}
                >
                  <Text style={styles.permBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.permBtn, { flex: 1, backgroundColor: '#dc2626' }]}
                  onPress={exitCallToReview}
                >
                  <Text style={styles.permBtnText}>End</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {renderEvaluationModal()}
      </View>
    );
  };

  // Evaluation modal — used by both prep "Mark Complete" and in-call end
  const renderEvaluationModal = () => (
    <Modal visible={showEvaluation} animationType="slide" transparent onRequestClose={() => setShowEvaluation(false)}>
      <View style={styles.modalOverlay}>
        <View style={[styles.modal, { maxHeight: '85%' }]}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>How was the interview?</Text>
            <TouchableOpacity onPress={() => setShowEvaluation(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>          <ScrollView>
            <Text style={styles.evalLabel}>Overall experience</Text>
            <View style={styles.starRow}>
              {[1,2,3,4,5].map(n => (
                <TouchableOpacity key={n} onPress={() => setFeedback(f => ({ ...f, rating: n }))}>
                  <Ionicons
                    name={n <= feedback.rating ? 'star' : 'star-outline'}
                    size={32}
                    color="#f59e0b"
                    style={{ marginRight: 6 }}
                  />
                </TouchableOpacity>
              ))}
            </View>

            <Text style={[styles.evalLabel, { marginTop: 16 }]}>Would you recommend this company?</Text>
            <View style={{ flexDirection: 'row', marginTop: 8 }}>
              {[
                { v: true,  label: 'Yes' },
                { v: false, label: 'No' },
              ].map(opt => (
                <TouchableOpacity
                  key={opt.label}
                  style={[
                    styles.recOpt,
                    feedback.wouldRecommend === opt.v && styles.recOptActive,
                  ]}
                  onPress={() => setFeedback(f => ({ ...f, wouldRecommend: opt.v }))}
                >
                  <Text style={[
                    styles.recOptText,
                    feedback.wouldRecommend === opt.v && { color: '#fff' },
                  ]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={[styles.evalLabel, { marginTop: 16 }]}>Comments (optional)</Text>
            <TextInput
              style={styles.notesInput}
              placeholder="Anything else you'd like to share…"
              value={feedback.comments}
              onChangeText={t => setFeedback(f => ({ ...f, comments: t }))}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />

            <TouchableOpacity style={[styles.savePrepBtn, { marginTop: 16 }]} onPress={submitFeedback}>
              <Text style={styles.savePrepBtnText}>Submit Feedback</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ─────────────────────────────────────────────────────────────────────
  // ROOT
  // ─────────────────────────────────────────────────────────────────────
  if (mode === 'incall')  return renderInCall();
  if (mode === 'prep')    return renderPrep();
  return renderLobby();
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────
function DetailRow({ icon, label, value }) {
  return (
    <View style={styles.detailRow}>
      <Ionicons name={icon} size={16} color="#64748B" style={{ marginRight: 8 }} />
      <Text style={styles.detailRowLabel}>{label}</Text>
      <Text style={styles.detailRowValue} numberOfLines={2}>{value}</Text>
    </View>
  );
}

function CallControl({ icon, active, danger, onPress }) {
  return (
    <TouchableOpacity
      style={[
        styles.callBtn,
        active && styles.callBtnActive,
        danger && styles.callBtnDanger,
      ]}
      onPress={onPress}
    >
      <Ionicons name={icon} size={22} color="#fff" />
    </TouchableOpacity>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F8FAFC' },
  center:   { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  muted:    { color: '#64748B', fontSize: 13, textAlign: 'center', marginTop: 8 },
  emptyTitle:{ fontSize: 16, fontWeight: '700', color: '#0f172a', marginTop: 12, marginBottom: 4 },

  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 12, paddingVertical: 12, backgroundColor: '#0e4e96',
  },
  backBtn: { padding: 8 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#fff', flex: 1, textAlign: 'center' },

  offlineBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#fff3cd', paddingHorizontal: 16, paddingVertical: 8,
  },
  offlineText: { marginLeft: 8, color: '#856404', fontSize: 12 },

  tabBar: { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  tabBtn:  { flex: 1, paddingVertical: 12, alignItems: 'center' },
  tabBtnActive: { borderBottomWidth: 2, borderBottomColor: '#0e4e96' },
  tabBtnText:   { fontSize: 13, color: '#64748B', fontWeight: '500' },
  tabBtnTextActive: { color: '#0e4e96', fontWeight: '700' },

  // Lobby card
  lobbyCard: {
    backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 12,
    borderWidth: 1, borderColor: '#E2E8F0',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3,
    elevation: 1,
  },
  lobbyCardHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  lobbyLogo: { width: 40, height: 40, borderRadius: 8, marginRight: 10, backgroundColor: '#F1F5F9' },
  lobbyPosition: { fontSize: 15, fontWeight: '700', color: '#0f172a' },
  lobbyCompany:  { fontSize: 13, color: '#475569', marginTop: 1 },
  statusPill: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10 },
  statusPillText: { fontSize: 10, fontWeight: '700', textTransform: 'capitalize' },

  lobbyMeta: { marginBottom: 8 },
  lobbyMetaItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  lobbyMetaText: { fontSize: 12, color: '#64748B', marginLeft: 6 },

  lobbyCountdown: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#E0F2FE', borderColor: '#BAE6FD', borderWidth: 1,
    borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6,
    alignSelf: 'flex-start', marginBottom: 8,
  },
  lobbyCountdownText: { fontSize: 12, color: '#0e4e96', fontWeight: '600', marginLeft: 4 },

  interviewerStrip: { flexDirection: 'row', alignItems: 'center' },
  interviewerAvatar: {
    width: 24, height: 24, borderRadius: 12,
    backgroundColor: '#0e4e96', alignItems: 'center', justifyContent: 'center',
    borderWidth: 2, borderColor: '#fff',
  },
  interviewerInitial: { color: '#fff', fontSize: 11, fontWeight: '700' },
  interviewerList: { marginLeft: 8, fontSize: 11, color: '#64748B', flex: 1 },

  // Prep hero
  prepHero: { paddingHorizontal: 20, paddingVertical: 24, alignItems: 'center' },
  prepLogo: { width: 56, height: 56, borderRadius: 12, backgroundColor: '#fff', marginBottom: 10 },
  prepCompany: { fontSize: 16, color: 'rgba(255,255,255,0.9)' },
  prepRole: { fontSize: 20, fontWeight: '700', color: '#fff', marginTop: 4 },
  prepCountdown: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 14, paddingVertical: 8,
    borderRadius: 20, marginTop: 12,
  },
  prepCountdownText: { color: '#fff', fontWeight: '600', marginLeft: 6, fontSize: 13 },
  joinBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#fff', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 24,
    marginTop: 16,
  },
  joinBtnDisabled: { backgroundColor: 'rgba(255,255,255,0.15)' },
  joinBtnText: { fontSize: 14, fontWeight: '700', color: '#0e4e96', marginLeft: 8 },
  markCompleteBtn: {
    marginTop: 10, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 16,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.6)',
  },
  markCompleteBtnText: { color: '#fff', fontSize: 12, fontWeight: '600' },
  rescheduleBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    marginTop: 10, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 16,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.6)',
  },
  reschedulePendingBtn: { backgroundColor: 'rgba(245,158,11,0.25)', borderColor: 'rgba(245,158,11,0.8)' },
  rescheduleBtnText: { color: '#fff', fontSize: 12, fontWeight: '600', marginLeft: 6 },

  prepSection: { padding: 16, backgroundColor: '#fff', marginTop: 10 },
  prepSectionTitle: { fontSize: 14, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  prepSectionSub:   { fontSize: 12, color: '#64748B', marginBottom: 10 },

  detailRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  detailRowLabel: { fontSize: 12, color: '#64748B', width: 88 },
  detailRowValue: { fontSize: 13, color: '#0f172a', flex: 1, fontWeight: '500' },

  instructionsBox: {
    backgroundColor: '#F8FAFC', borderRadius: 8, padding: 12,
    borderLeftWidth: 3, borderLeftColor: '#0e4e96',
  },
  instructionsText: { fontSize: 13, color: '#334155', lineHeight: 18 },

  interviewerRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F1F5F9',
  },
  interviewerRowAvatar: {
    width: 40, height: 40, borderRadius: 20, backgroundColor: '#0e4e96',
    alignItems: 'center', justifyContent: 'center', marginRight: 12,
  },
  interviewerRowInitial: { color: '#fff', fontSize: 16, fontWeight: '700' },
  interviewerRowName:    { fontSize: 14, fontWeight: '600', color: '#0f172a' },
  interviewerRowTitle:   { fontSize: 12, color: '#64748B', marginTop: 2 },

  notesInput: {
    backgroundColor: '#F8FAFC', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8,
    padding: 12, fontSize: 14, minHeight: 100,
  },
  savePrepBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#0e4e96', paddingVertical: 12, borderRadius: 8, marginTop: 12,
  },
  savePrepBtnText: { color: '#fff', fontWeight: '700', marginLeft: 6 },

  recordingBtn: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#E0F2FE', padding: 12, borderRadius: 8,
  },
  recordingBtnText: { color: '#0e4e96', fontWeight: '600', marginLeft: 8 },

  // In-call
  callRoot: { flex: 1, backgroundColor: '#000', justifyContent: 'space-between' },
  callTopBar: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 16, paddingTop: 48, paddingBottom: 12,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  connectionDotRow: { flexDirection: 'row', alignItems: 'center', width: 130 },
  connectionDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
  connectionText: { color: '#fff', fontSize: 11 },
  callTimer: { color: '#fff', fontSize: 18, fontWeight: '700', textAlign: 'center' },
  callSub:   { color: '#94a3b8', fontSize: 11, textAlign: 'center' },
  recBadge: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(220,38,38,0.85)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12,
    width: 80, justifyContent: 'center',
  },
  recDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff', marginRight: 4 },
  recText: { color: '#fff', fontSize: 10, fontWeight: '700' },

  cameraArea: { flex: 1 },
  cameraPlaceholder: { flex: 1, backgroundColor: '#111827', alignItems: 'center', justifyContent: 'center' },
  cameraPlaceholderText: { color: '#475569', fontSize: 13, marginTop: 8 },

  callControls: {
    flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center',
    paddingVertical: 24, paddingHorizontal: 12, backgroundColor: 'rgba(0,0,0,0.55)',
  },
  callBtn: {
    width: 48, height: 48, borderRadius: 24, backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  callBtnActive: { backgroundColor: '#0e4e96' },
  callBtnDanger: { backgroundColor: '#dc2626' },
  endCallBtn:    { backgroundColor: '#dc2626' },

  permBtn: { backgroundColor: '#0e4e96', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 8 },
  permBtnText: { color: '#fff', fontWeight: '700' },

  // Modals
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  modal: {
    backgroundColor: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16,
    padding: 16, maxHeight: '70%',
  },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  modalTitle:  { fontSize: 16, fontWeight: '700', color: '#0f172a' },

  evalLabel: { fontSize: 13, fontWeight: '600', color: '#475569', marginTop: 8 },
  starRow:   { flexDirection: 'row', marginTop: 8 },
  recOpt:    { paddingHorizontal: 20, paddingVertical: 8, borderRadius: 16, borderWidth: 1, borderColor: '#CBD5E1', marginRight: 8 },
  recOptActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  recOptText:   { color: '#475569', fontWeight: '600' },
});
